import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Play, Pause, Plus, Trash2, ListMusic, X, SkipForward, SkipBack, Music2, Square, ExternalLink, Pencil, Check } from "lucide-react";
import { toast } from "sonner";

export default function Music() {
  const [playlists, setPlaylists] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [newPl, setNewPl] = useState("");
  const [songUrl, setSongUrl] = useState("");
  const [state, setState] = useState(null); // server player state
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState("");

  const load = async () => {
    const r = await api.get("/playlists");
    setPlaylists(r.data);
    if (!activeId && r.data.length) setActiveId(r.data[0].id);
  };
  const loadState = async () => {
    const r = await api.get("/player/state");
    setState(r.data);
  };
  useEffect(() => { load(); loadState(); const t = setInterval(loadState, 2000); return () => clearInterval(t); }, []);

  const active = playlists.find((p) => p.id === activeId);
  const playingSong = state?.playlist?.songs?.[state?.index];

  const createPl = async () => {
    if (!newPl.trim()) return;
    const r = await api.post("/playlists", { name: newPl });
    setNewPl(""); setActiveId(r.data.id); load();
    toast.success("Playlist created");
  };
  const delPl = async (id) => { await api.delete(`/playlists/${id}`); if (activeId === id) setActiveId(null); load(); };
  const savePlName = async () => {
    if (!renameVal.trim() || !active) { setRenaming(false); return; }
    await api.post(`/playlists/${active.id}/rename`, { name: renameVal.trim() });
    setRenaming(false); load();
    toast.success("Playlist renamed");
  };
  const addSong = async () => {
    if (!songUrl.trim() || !active) return;
    try {
      const r = await api.post(`/playlists/${active.id}/songs`, { url: songUrl });
      setSongUrl(""); load();
      if (r.data.imported) toast.success(`Imported ${r.data.imported} songs from playlist 🎶`);
      else toast.success("Song added");
    }
    catch (e) { toast.error(e.response?.data?.detail || "Invalid YouTube link"); }
  };
  const removeSong = async (sid) => { await api.delete(`/playlists/${active.id}/songs/${sid}`); load(); };

  const playAll = async () => { await api.post("/player/play", { playlist_id: active.id, index: 0 }); loadState(); toast.success("Playing on overlay ▶"); };
  const playFrom = async (i) => { await api.post("/player/play", { playlist_id: active.id, index: i }); loadState(); };
  const pause = async () => { await api.post("/player/pause"); loadState(); };
  const resume = async () => { await api.post("/player/resume"); loadState(); };
  const next = async () => { await api.post("/player/next"); loadState(); };
  const prev = async () => { await api.post("/player/prev"); loadState(); };
  const stop = async () => { await api.post("/player/stop"); loadState(); };

  return (
    <div className="space-y-6 pb-32">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-800 font-heading">Music Player</h1>
          <p className="text-muted-foreground mt-1 text-sm">Build playlists and control playback on your <span className="text-primary">OBS overlay</span>.</p>
        </div>
        <a href="/overlay" target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1 text-primary hover:underline" data-testid="open-overlay-link">
          <ExternalLink className="w-3.5 h-3.5" /> Open Stream Overlay (audio plays here)
        </a>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Playlists sidebar */}
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-4">
          <div className="flex gap-2 mb-4">
            <Input value={newPl} onChange={(e) => setNewPl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createPl()} placeholder="New playlist" className="bg-secondary border-white/10 h-9" data-testid="new-playlist-input" />
            <Button onClick={createPl} size="sm" className="bg-primary hover:bg-primary/90" data-testid="create-playlist-button"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-1" data-testid="playlist-list">
            {playlists.map((p) => (
              <div key={p.id} className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors duration-200 ${activeId === p.id ? "bg-primary/15 border border-primary/30" : "hover:bg-white/5"}`} onClick={() => setActiveId(p.id)} data-testid={`playlist-${p.name}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <ListMusic className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-sm truncate">{p.name}</span>
                  <span className="text-xs text-muted-foreground">({p.songs?.length || 0})</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); delPl(p.id); }} className="text-muted-foreground hover:text-red-400" data-testid={`delete-playlist-${p.name}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {playlists.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No playlists yet.</p>}
          </div>
        </div>

        {/* Active playlist */}
        <div className="lg:col-span-2 bg-[#0a0a0a] border border-white/10 rounded-lg p-4">
          {active ? (
            <>
              <div className="flex items-center justify-between mb-4">
                {renaming ? (
                  <div className="flex items-center gap-2 flex-1 mr-2">
                    <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") savePlName(); if (e.key === "Escape") setRenaming(false); }} autoFocus className="bg-secondary border-white/10 h-9" data-testid="rename-playlist-input" />
                    <Button onClick={savePlName} size="sm" className="bg-primary hover:bg-primary/90 h-9" data-testid="rename-playlist-save"><Check className="w-4 h-4" /></Button>
                    <Button onClick={() => setRenaming(false)} size="sm" variant="ghost" className="h-9"><X className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-700 text-lg">{active.name}</h3>
                    <button onClick={() => { setRenameVal(active.name); setRenaming(true); }} className="text-muted-foreground hover:text-primary" title="Rename playlist" data-testid="rename-playlist-button"><Pencil className="w-3.5 h-3.5" /></button>
                  </div>
                )}
                <Button onClick={playAll} disabled={!active.songs?.length} size="sm" className="bg-primary hover:bg-primary/90 rounded-full" data-testid="play-playlist-button">
                  <Play className="w-4 h-4 mr-1" /> Play All
                </Button>
              </div>
              <div className="flex gap-2 mb-4">
                <Input value={songUrl} onChange={(e) => setSongUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSong()} placeholder="Paste a YouTube video OR playlist link…" className="bg-secondary border-white/10" data-testid="song-url-input" />
                <Button onClick={addSong} className="bg-cookie text-black hover:bg-cookie/90" data-testid="add-song-button"><Plus className="w-4 h-4 mr-1" />Add</Button>
              </div>
              <div className="space-y-2" data-testid="song-list">
                {active.songs?.map((s, i) => (
                  <div key={s.id} className={`flex items-center gap-3 p-2 rounded-lg group ${playingSong?.id === s.id ? "bg-primary/15 border border-primary/30" : "hover:bg-white/5"}`} data-testid="song-row">
                    <img src={s.thumbnail} alt="" className="w-16 h-10 object-cover rounded" />
                    <span className="text-sm flex-1 truncate">{s.title}</span>
                    <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-primary" onClick={() => playFrom(i)} data-testid={`play-song-${i}`}><Play className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-red-400" onClick={() => removeSong(s.id)} data-testid={`remove-song-${i}`}><X className="w-4 h-4" /></Button>
                  </div>
                ))}
                {(!active.songs || active.songs.length === 0) && <p className="text-sm text-muted-foreground text-center py-10">Add some YouTube songs to get started.</p>}
              </div>
            </>
          ) : <p className="text-sm text-muted-foreground text-center py-16">Select or create a playlist.</p>}
        </div>
      </div>

      {/* Sticky control bar (controls the overlay) */}
      {state?.playlist_id && playingSong && (
        <div className="fixed bottom-0 left-0 md:left-64 right-0 bg-black/90 backdrop-blur-xl border-t border-white/10 z-40 p-3" data-testid="player-bar">
          <div className="max-w-[1500px] mx-auto flex items-center gap-4">
            <img src={playingSong.thumbnail} alt="" className={`w-14 h-14 rounded-full object-cover shrink-0 ${state.is_playing ? "spin-slow" : ""}`} style={{ border: "2px solid #FF3366" }} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-primary text-xs"><Music2 className="w-3.5 h-3.5" /> {state.is_playing ? "Playing on overlay" : "Paused"} · {state.playlist?.name}</div>
              <p className="font-medium truncate mt-1">{playingSong.title}</p>
              <p className="text-xs text-muted-foreground">Track {state.index + 1} of {state.playlist?.songs?.length}</p>
            </div>
            <div className="flex gap-1 items-center">
              <Button size="icon" variant="ghost" onClick={prev} data-testid="player-prev" className="hover:text-primary"><SkipBack className="w-5 h-5" /></Button>
              {state.is_playing ? (
                <Button size="icon" onClick={pause} data-testid="player-pause" className="bg-primary hover:bg-primary/90 rounded-full h-11 w-11"><Pause className="w-5 h-5" /></Button>
              ) : (
                <Button size="icon" onClick={resume} data-testid="player-play" className="bg-primary hover:bg-primary/90 rounded-full h-11 w-11"><Play className="w-5 h-5" /></Button>
              )}
              <Button size="icon" variant="ghost" onClick={next} data-testid="player-next" className="hover:text-primary"><SkipForward className="w-5 h-5" /></Button>
              <Button size="icon" variant="ghost" onClick={stop} data-testid="player-stop" className="hover:text-red-400"><Square className="w-5 h-5" /></Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
